import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { Modal, Input, Spin, Button as Btn } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "contexts/TranslationContext";
import useAuthHook from "hooks/useAuthHook";
import { useCheckEligibilityMutation, useLazyGetSalaryQuery } from "services/auth";
import routes, { appRoute } from "shared/constants/routes";
import { isSalaryInRange } from "shared/constants/salary";
import SaudiRiyal from "assets/svg/saudi_riyal_symbol.svg";
import SalaryInfoImg from "assets/svg/SalaryInfo.png";
import SalaryNotEligibleImg from "assets/svg/SalaryInfoNotElig.png";

// Handoff for the "recommended product" links. The eligibility response already
// told us the user qualifies for the product they're about to open, so we mark
// it cleared and skip re-checking on arrival. Read once, then discarded. Uses
// sessionStorage rather than component state because the target product may be
// rendered by a different page component (Vouchers → Finance).
const CLEARED_KEY = "salaryEligibilityClearedFor";

// Product catalog for the eligibility gate. `flag` maps to the key returned in
// `recommended_eligibility`; `route` is where the alternative-product link goes.
// Murabaha has no route on purpose — the flag covers several products
// (Vouchers / School / Dental), so it is shown as plain text, not a link.
export const ELIGIBILITY_PRODUCTS = {
  TWQ: { flag: "twq", labelKey: "SALARY_ELIGIBILITY.PRODUCT_TWQ", route: `${routes.PRODUCTS}/2` },
  MICRO: { flag: "micro", labelKey: "SALARY_ELIGIBILITY.PRODUCT_MICRO", route: `${routes.PRODUCTS}/3` },
  IJARAH: { flag: "ijarah", labelKey: "SALARY_ELIGIBILITY.PRODUCT_IJARAH", route: `${routes.PRODUCTS}/1` },
  MUR: { flag: "mur", labelKey: "SALARY_ELIGIBILITY.PRODUCT_MUR", route: null },
};

/**
 * Salary eligibility gate shown when a product application form opens.
 *
 * Reads the salary already on file (app/get-salary) and checks it against the
 * product's minimum automatically. Only customers with no salary on record are
 * asked to type one in — the backend captures that first value as part of
 * check-eligibility, so this component never calls update-salary itself.
 *
 * @param {string} productCode - "TWQ" | "MICRO" | "IJARAH" | "MUR"
 */
const SalaryEligibilityGate = ({ productCode }) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { getUser } = useAuthHook();
  const [checkEligibility, { isLoading: isChecking }] = useCheckEligibilityMutation();
  const [fetchSalary] = useLazyGetSalaryQuery();

  // "loading" → resolving salary/eligibility · "salary" → asking for salary
  // "notEligible" → result · "passed" → gate cleared
  const [step, setStep] = useState("loading");
  const [salary, setSalary] = useState("");
  const [alternatives, setAlternatives] = useState([]);

  const userId = getUser()?.IDB_USR_ID;

  // Run the eligibility check and route to the right outcome.
  const runCheck = async (numeric) => {
    try {
      const res = await checkEligibility({
        salary: numeric,
        productcode: productCode,
      }).unwrap();

      if (res?.success) {
        setStep("passed");
        return;
      }

      // Not eligible for this product — surface the others they qualify for,
      // excluding the product they're already on.
      const flags = res?.recommended_eligibility || {};
      const eligible = Object.entries(ELIGIBILITY_PRODUCTS)
        .filter(([code, cfg]) => code !== productCode && Number(flags[cfg.flag]) === 1)
        .map(([code, cfg]) => ({ code, ...cfg }));

      setAlternatives(eligible);
      setStep("notEligible");
    } catch (err) {
      console.error("Eligibility check failed:", err);
      toast.error(
        err?.data?.error?.errorMessage ||
        err?.data?.error?.message ||
        err?.data?.message ||
        translate("SALARY_ELIGIBILITY.CHECK_FAILED")
      );
      // Let them retry rather than stranding them on a blank gate.
      setStep("salary");
    }
  };

  // Re-arm whenever the product changes. Following a recommended-product link
  // swaps the route param without unmounting this page, so without this the
  // modal would stay open over the newly-loaded form.
  useEffect(() => {
    let cancelled = false;

    const arm = async () => {
      let cleared = null;
      try {
        cleared = sessionStorage.getItem(CLEARED_KEY);
      } catch (err) {
        console.error("Unable to read eligibility handoff:", err);
      }

      // Strictly one-shot: consume it whether or not it matches, so a handoff
      // the user navigated away from can't skip the gate on a later visit.
      if (cleared != null) {
        try {
          sessionStorage.removeItem(CLEARED_KEY);
        } catch (err) {
          console.error("Unable to clear eligibility handoff:", err);
        }
      }

      if (cleared === productCode) {
        setStep("passed");
        return;
      }

      setAlternatives([]);
      setSalary("");
      setStep("loading");

      // Use the salary already on file so the user isn't asked every time.
      let storedSalary = null;
      try {
        const res = await fetchSalary({ id: Number(userId) }).unwrap();
        storedSalary = res?.result?.SALARY;
      } catch (err) {
        console.error("Failed to read stored salary:", err);
      }

      if (cancelled) return;

      if (storedSalary != null) {
        await runCheck(Number(storedSalary));
      } else {
        // Nothing on record — ask once; check-eligibility captures it server-side.
        setStep("salary");
      }
    };

    arm();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, userId]);

  const goHome = () => navigate(appRoute(routes.HOME));

  // Dismissing the gate without checking returns the user where they came from.
  const handleDismiss = () => navigate(-1);

  const handleProceed = () => {
    if (!isSalaryInRange(salary)) {
      toast.error(translate("SALARY_ELIGIBILITY.SALARY_OUT_OF_RANGE"));
      return;
    }
    setStep("loading");
    runCheck(Number(salary));
  };

  // Opening a recommended product: close this gate straight away and hand off
  // the "already eligible" signal so the target form doesn't re-check.
  const handleAlternativeClick = (item) => {
    try {
      sessionStorage.setItem(CLEARED_KEY, item.code);
    } catch (err) {
      console.error("Unable to store eligibility handoff:", err);
    }
    setStep("passed");
    navigate(appRoute(item.route));
  };

  if (step === "passed") return null;

  // "…you're eligible for Microfinance and Ijarah." — linked where a product
  // maps to a single destination, plain text otherwise (Murabaha).
  const renderAlternatives = () =>
    alternatives.map((item, index) => {
      const isLast = index === alternatives.length - 1;
      const separator = index === 0 ? "" : isLast ? ` ${translate("SALARY_ELIGIBILITY.AND")} ` : ", ";

      const label = translate(item.labelKey);
      return (
        <span key={item.code}>
          {separator}
          {item.route ? (
            <button
              type="button"
              className="text-primary font-semibold hover:opacity-80"
              onClick={() => handleAlternativeClick(item)}
            >
              {label}
            </button>
          ) : (
            <span className="text-primary font-semibold">{label}</span>
          )}
        </span>
      );
    });

  return (
    <>
      {/* Resolving the salary on file / running the eligibility check */}
      <Modal
        open={step === "loading"}
        footer={null}
        closable={false}
        centered
        width={320}
        maskClosable={false}
        bodyStyle={{ padding: "40px" }}
      >
        <div className="flex justify-center items-center">
          <Spin size="large" />
        </div>
      </Modal>

      {/* Asked only when there's no salary on record */}
      <Modal
        open={step === "salary"}
        onCancel={handleDismiss}
        footer={null}
        closable
        closeIcon={<CloseOutlined style={{ fontSize: "16px", color: "#000" }} />}
        centered
        width={480}
        maskClosable={false}
        bodyStyle={{ padding: "32px" }}
      >
        <div className="flex flex-col items-center text-center">
          <img src={SalaryInfoImg} alt="" className="w-48 md:w-56 mb-4" />
          <h2 className="text-2xl md:text-[28px] font-semibold text-[#1F1F1F] mb-3">
            {translate("SALARY_ELIGIBILITY.TITLE")}
          </h2>
          <p className="text-[#8E9196] text-sm md:text-base mb-6">
            {translate("SALARY_ELIGIBILITY.DESC")}
          </p>

          <div className="w-full text-start mb-6">
            <label className="block text-[#5F5F5F] text-sm mb-1">
              {translate("SALARY_ELIGIBILITY.CURRENT_SALARY")}
            </label>
            <Input
              value={salary}
              onChange={(e) => setSalary(e.target.value.replace(/[^0-9]/g, ""))}
              onPressEnter={handleProceed}
              placeholder={translate("SALARY_ELIGIBILITY.SALARY_PLACEHOLDER")}
              size="large"
              inputMode="numeric"
              className="rounded-lg"
              prefix={<img src={SaudiRiyal} alt="" className="w-4 h-4" />}
            />
          </div>

          <Btn
            disabled={isChecking}
            onClick={handleProceed}
            className="w-full max-w-[240px] rounded-full bg-primary text-white font-semibold py-5 border border-primary disabled:opacity-60 hover:!border-primary hover:border-solid hover:!text-primary"
          >
            {isChecking ? <LoadingOutlined /> : translate("SALARY_ELIGIBILITY.PROCEED")}
          </Btn>
        </div>
      </Modal>

      {/* Not eligible (with or without alternatives) */}
      <Modal
        open={step === "notEligible"}
        onCancel={goHome}
        footer={null}
        closable
        closeIcon={<CloseOutlined style={{ fontSize: "16px", color: "#000" }} />}
        centered
        width={480}
        maskClosable={false}
        bodyStyle={{ padding: "32px" }}
      >
        <div className="flex flex-col items-center text-center">
          <img src={SalaryNotEligibleImg} alt="" className="w-48 md:w-56 mb-4" />
          <h2 className="text-2xl md:text-[28px] font-semibold text-[#1F1F1F] mb-3">
            {translate("SALARY_ELIGIBILITY.NOT_ELIGIBLE_TITLE")}
          </h2>
          <p className="text-[#8E9196] text-sm md:text-base mb-8 max-w-sm">
            {alternatives.length > 0 ? (
              <>
                {translate("SALARY_ELIGIBILITY.NOT_ELIGIBLE_ALT_PREFIX")}{" "}
                {renderAlternatives()}.
              </>
            ) : (
              translate("SALARY_ELIGIBILITY.NOT_ELIGIBLE_DESC")
            )}
          </p>

          <Btn
            onClick={goHome}
            className="w-full max-w-[240px] rounded-full bg-primary text-white font-semibold py-5 border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
          >
            {translate("SALARY_ELIGIBILITY.CONTINUE")}
          </Btn>
        </div>
      </Modal>
    </>
  );
};

export default SalaryEligibilityGate;
